import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import {
  AnchoredAlert,
  Flex,
  Heading,
  PortalContext,
  Text,
} from '@amzn/storm-ui';
import {
  useComponentExpiry,
  useDismissibleComponent,
} from '@amzn/storm-ui-utils';
import {
  CAMPAIGN_MANAGER_TOOLBAR_ID,
  NAV_MIGRATION_ANNOUNCEMENT_ID,
} from 'src/commons/constants';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { ACCORDION_WRAPPER_WIDTH_PX, RAIL_WIDTH_EXPANDED_PX } from '../styles';

const ANNOUNCEMENT_CONTENT_WIDTH_PX = 320;
const CLOSED_TRIGGER_OVERSHOOT_PX = 7;
const POPPER_ARROW_RESERVED_LATERAL_PX = 4;
const RAIL_TO_TRIGGER_TRAILING_GAP_OPEN_PX =
  RAIL_WIDTH_EXPANDED_PX - ACCORDION_WRAPPER_WIDTH_PX;
const ANCHOR_SPACING_NAV_CLOSED_PX = -CLOSED_TRIGGER_OVERSHOOT_PX;
const ANCHOR_SPACING_NAV_OPEN_PX =
  RAIL_TO_TRIGGER_TRAILING_GAP_OPEN_PX - POPPER_ARROW_RESERVED_LATERAL_PX;
const NAV_MIGRATION_EXPIRY_DATE = '2027-01-01';
const HEADING_LEVEL = 'h4' as const;
const ANNOUNCEMENT_POSITION = 'right' as const;
const ANNOUNCEMENT_ALIGNMENT = 'center' as const;
const ANNOUNCEMENT_VARIANT = 'spotlight' as const;
const ANNOUNCEMENT_E2E_ID_PREFIX = 'aac-nav-announcement-';

const ANNOUNCEMENT_SHOWN_METRIC = 'aac-chrome-nav-announcement-shown';
const ANNOUNCEMENT_DISMISSED_METRIC = 'aac-chrome-nav-announcement-dismissed';

const AnnouncementContent = styled(Flex)`
  width: ${ANNOUNCEMENT_CONTENT_WIDTH_PX}px;
`;

const buildE2eId = (announcementId: string): string =>
  `${ANNOUNCEMENT_E2E_ID_PREFIX}${announcementId}`;

const buildHeadingElementId = (announcementId: string): string =>
  `${ANNOUNCEMENT_E2E_ID_PREFIX}heading-${announcementId}`;

const dispatchResizeToRepositionPopper = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('resize'));
};

const useRepositionOnTriggerResize = (
  triggerSelector: string,
  isActive: boolean,
): void => {
  useEffect(() => {
    if (!isActive || typeof ResizeObserver === 'undefined') return;
    let observer: ResizeObserver | null = null;
    let observedElement: Element | null = null;
    let pollForTrigger: number | null = null;
    const tryObserve = () => {
      const el = document.querySelector(triggerSelector);
      if (!el || el === observedElement) return;
      observer?.disconnect();
      observedElement = el;
      observer = new ResizeObserver(() => dispatchResizeToRepositionPopper());
      observer.observe(el);
      if (pollForTrigger !== null) {
        window.clearInterval(pollForTrigger);
        pollForTrigger = null;
      }
    };
    tryObserve();
    if (!observedElement) {
      pollForTrigger = window.setInterval(tryObserve, 200);
    }
    return () => {
      if (pollForTrigger !== null) {
        window.clearInterval(pollForTrigger);
      }
      observer?.disconnect();
    };
  }, [triggerSelector, isActive]);
};

export interface NavigationAnnouncementProps {
  announcementId: string;
  children: ReactElement;
  enabled?: boolean;
}

const ANCHOR_TRIGGER_SELECTOR = `[data-ccx-e2e-id="${CAMPAIGN_MANAGER_TOOLBAR_ID}"]`;

export const NavigationAnnouncement: React.FC<NavigationAnnouncementProps> = ({
  announcementId,
  children,
  enabled = true,
}) => {
  const bundle = useSidenavBundle();
  const portalRef = useContext(PortalContext);
  const { isNavOpen } = useConsolidatedNavContext();

  const [isDismissed, dismissAnnouncement] =
    useDismissibleComponent(announcementId);
  const isExpired = useComponentExpiry(NAV_MIGRATION_EXPIRY_DATE);

  const isVisible = enabled && !isDismissed && !isExpired;
  const headingElementId = useMemo(
    () => buildHeadingElementId(announcementId),
    [announcementId],
  );
  const e2eId = useMemo(() => buildE2eId(announcementId), [announcementId]);

  useRepositionOnTriggerResize(ANCHOR_TRIGGER_SELECTOR, isVisible);

  const shownMetricEmittedRef = useRef(false);
  useEffect(() => {
    if (isVisible && !shownMetricEmittedRef.current) {
      shownMetricEmittedRef.current = true;
      logWithTakt(ANNOUNCEMENT_SHOWN_METRIC, { announcementId });
    }
  }, [isVisible, announcementId]);

  useEffect(() => {
    if (isVisible) {
      dispatchResizeToRepositionPopper();
    }
  }, [isNavOpen, isVisible]);

  const handleDismiss = () => {
    dismissAnnouncement();
    logWithTakt(ANNOUNCEMENT_DISMISSED_METRIC, { announcementId });
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <AnchoredAlert
      id={announcementId}
      trigger={children}
      position={ANNOUNCEMENT_POSITION}
      align={ANNOUNCEMENT_ALIGNMENT}
      variant={ANNOUNCEMENT_VARIANT}
      fullWidthTrigger={isNavOpen}
      spacing={
        isNavOpen ? ANCHOR_SPACING_NAV_OPEN_PX : ANCHOR_SPACING_NAV_CLOSED_PX
      }
      isOpen={isVisible}
      onCloseButtonClick={handleDismiss}
      withCloseButton
      closeButtonLabel={bundle.getMessage(
        'sidenav-nav-announcement-close-label',
      )}
      portalRef={portalRef}
      aria-labelledby={headingElementId}
      data-ccx-e2e-id={e2eId}
    >
      <AnnouncementContent direction="column" rowGap="xsmall">
        <Heading id={headingElementId} renderAs={HEADING_LEVEL}>
          {bundle.getMessage('sidenav-nav-announcement-header')}
        </Heading>
        <Text renderAs="p">
          {bundle.getMessage('sidenav-nav-announcement-body')}
        </Text>
      </AnnouncementContent>
    </AnchoredAlert>
  );
};

export default NavigationAnnouncement;

export { NAV_MIGRATION_ANNOUNCEMENT_ID };
